import { createMajorApi } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CreateMajorFormTable } from "@/features/master-data/components/JobType/CreateMajorFormTable";
import { masterDataCreateMajorSchema } from "@/features/master-data/schema/masterDataJobTypeSchema";
import { TMasterMajorCreateForm } from "@/features/master-data/types/masterDataJobTypeForm";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormLabel, Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";

type TCreateMajorFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

const defaultFormValues: TMasterMajorCreateForm = {
  majorName: "",
  jobTypes: [
    {
      jobName: "",
    },
  ],
};

export const CreateMajorFormModal: FC<TCreateMajorFormModalProps> = ({ onClose, isOpen }) => {
  const queryClient = useQueryClient();

  const methods = useForm<TMasterMajorCreateForm>({
    resolver: yupResolver(masterDataCreateMajorSchema),
    mode: "onBlur",
    defaultValues: defaultFormValues,
  });

  const { control, reset, watch } = methods;

  const { append, remove } = useFieldArray({
    control,
    name: "jobTypes",
  });

  useEffect(() => {
    if (isOpen) {
      reset(defaultFormValues);
      remove();
      append({ jobName: "" });
    }
  }, [isOpen, reset, remove, append]);

  const onSubmit = async (data: TMasterMajorCreateForm) => {
    try {
      const rs = await createMajorApi({
        majorName: data.majorName,
        jobTypes: data.jobTypes.map((item) => ({
          jobName: item.jobName,
        })),
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_MAJOR] });
      reset(defaultFormValues);
      remove();
      append({ jobName: "" });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal width={1200} title="追加" isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false}>
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataCreateMajorSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={6}>
            <FormField control={control} name="majorName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <Stack gap={2}>
              <FormField>
                <FormLabel required>作業</FormLabel>
              </FormField>
              <CreateMajorFormTable />
            </Stack>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit" disabled={watch("jobTypes").length === 0}>
            保存
          </Button>
        </Stack>
      </Form>
    </Modal>
  );
};
